import {
  AdMob,
  AdOptions,
  AdmobConsentStatus,
  BannerAdOptions,
  BannerAdPosition,
  BannerAdSize,
  InterstitialAdPluginEvents,
} from '@capacitor-community/admob';
import { LoggerService } from '../logger';
import { Capacitor } from '@capacitor/core';
import { BhabiStorage } from './storage';

/* ------------------------------------------------------------------------ *
 * Configuration
 * ------------------------------------------------------------------------ */

export type AdPlatform = 'ios' | 'android' | 'web';
export type AdKind = 'interstitial' | 'banner';

/**
 * Google's official sample ad units. They always fill with labelled test ads
 * and never pay out, so development/test builds can never generate invalid
 * traffic on the production units.
 * https://developers.google.com/admob/ios/test-ads
 * https://developers.google.com/admob/android/test-ads
 */
export const ADMOB_TEST_AD_UNITS: Record<
  Exclude<AdPlatform, 'web'>,
  Record<AdKind, string>
> = {
  ios: {
    interstitial: 'ca-app-pub-3940256099942544/4411468910',
    banner: 'ca-app-pub-3940256099942544/2934735716',
  },
  android: {
    interstitial: 'ca-app-pub-3940256099942544/1033173712',
    banner: 'ca-app-pub-3940256099942544/6300978111',
  },
};

/** Build-time env subset read by the ad config. CRA inlines REACT_APP_*. */
export interface AdEnv {
  readonly NODE_ENV?: string;
  readonly REACT_APP_ADMOB_TESTING?: string;
  readonly REACT_APP_FULL_PAGE_AD_UNIT_ID?: string;
  readonly REACT_APP_FULL_PAGE_AD_UNIT_ID_ANDROID?: string;
  readonly REACT_APP_BANNER_AD_UNIT_ID_IOS?: string;
  readonly REACT_APP_BANNER_AD_UNIT_ID_ANDROID?: string;
}

export interface AdUnitConfig {
  /** Empty when no unit is configured; callers must treat that as unavailable. */
  readonly adId: string;
  readonly isTesting: boolean;
}

/**
 * Test mode is forced for any non-production build (development, test) and
 * can be forced in a production build with REACT_APP_ADMOB_TESTING=true
 * (e.g. TestFlight QA). In test mode the configured production unit IDs are
 * ignored entirely.
 */
export const isAdTesting = (env: AdEnv): boolean =>
  env.NODE_ENV !== 'production' || env.REACT_APP_ADMOB_TESTING === 'true';

export const resolveAdUnit = (
  kind: AdKind,
  platform: AdPlatform,
  env: AdEnv
): AdUnitConfig => {
  if (platform === 'web') return { adId: '', isTesting: isAdTesting(env) };
  if (isAdTesting(env)) {
    return { adId: ADMOB_TEST_AD_UNITS[platform][kind], isTesting: true };
  }
  const id =
    kind === 'interstitial'
      ? platform === 'android'
        ? env.REACT_APP_FULL_PAGE_AD_UNIT_ID_ANDROID
        : env.REACT_APP_FULL_PAGE_AD_UNIT_ID
      : platform === 'android'
        ? env.REACT_APP_BANNER_AD_UNIT_ID_ANDROID
        : env.REACT_APP_BANNER_AD_UNIT_ID_IOS;
  return { adId: (id || '').trim(), isTesting: false };
};

// Direct references so CRA inlines the values at build time.
const buildEnv: AdEnv = {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_ADMOB_TESTING: process.env.REACT_APP_ADMOB_TESTING,
  REACT_APP_FULL_PAGE_AD_UNIT_ID: process.env.REACT_APP_FULL_PAGE_AD_UNIT_ID,
  REACT_APP_FULL_PAGE_AD_UNIT_ID_ANDROID:
    process.env.REACT_APP_FULL_PAGE_AD_UNIT_ID_ANDROID,
  REACT_APP_BANNER_AD_UNIT_ID_IOS: process.env.REACT_APP_BANNER_AD_UNIT_ID_IOS,
  REACT_APP_BANNER_AD_UNIT_ID_ANDROID:
    process.env.REACT_APP_BANNER_AD_UNIT_ID_ANDROID,
};

/* ------------------------------------------------------------------------ *
 * Interstitial result API
 * ------------------------------------------------------------------------ */

/** Lifecycle events reported while one interstitial attempt runs. */
export type InterstitialEvent = 'loading' | 'loaded' | 'shown' | 'dismissed';

export type InterstitialFailureStage =
  /** Not a native platform, ads disabled or no unit configured. */
  | 'unavailable'
  /** Consent (UMP) says ads may not be requested. */
  | 'consent'
  | 'load'
  | 'show';

export type InterstitialResult =
  | {
      readonly status: 'dismissed';
      readonly loaded: true;
      readonly shown: true;
    }
  | {
      readonly status: 'failed';
      readonly stage: InterstitialFailureStage;
      readonly loaded: boolean;
      readonly shown: boolean;
      readonly message: string;
    };

/**
 * The only result that unlocks a play attempt: the ad was actually displayed
 * and then dismissed. Load/show failures, timeouts and unavailability never
 * unlock (callers offer retry / non-ad alternatives instead).
 */
export const unlocksPlay = (r: InterstitialResult): boolean =>
  r.status === 'dismissed' && r.loaded && r.shown;

interface ListenerHandle {
  remove: () => Promise<void> | void;
}

/** Minimal surface of the AdMob plugin used by `runInterstitial` (testable). */
export interface InterstitialDriver {
  prepare(options: AdOptions): Promise<unknown>;
  show(): Promise<void>;
  onShowed(cb: () => void): Promise<ListenerHandle>;
  onDismissed(cb: () => void): Promise<ListenerHandle>;
  onFailedToShow(cb: (err: unknown) => void): Promise<ListenerHandle>;
}

export interface RunInterstitialOptions {
  readonly onEvent?: (e: InterstitialEvent) => void;
  /** Max time for the ad to load. */
  readonly loadTimeoutMs?: number;
  /** Max time between show() and the ad becoming visible. */
  readonly showTimeoutMs?: number;
  /** Max time an ad may stay on screen before we give up waiting. */
  readonly dismissTimeoutMs?: number;
}

const errMessage = (e: unknown): string =>
  e instanceof Error
    ? e.message
    : typeof e === 'object' && e && 'message' in e
      ? String((e as { message: unknown }).message)
      : String(e ?? 'unknown error');

const withTimeout = <T>(p: Promise<T>, ms: number, label: string) =>
  new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    p.then(
      v => {
        clearTimeout(t);
        resolve(v);
      },
      e => {
        clearTimeout(t);
        reject(e);
      }
    );
  });

/**
 * Loads and shows exactly one interstitial and resolves with an explicit
 * result. Never rejects and never hangs forever: every phase is time-boxed.
 */
export const runInterstitial = async (
  driver: InterstitialDriver,
  options: AdOptions,
  {
    onEvent,
    loadTimeoutMs = 20_000,
    showTimeoutMs = 10_000,
    dismissTimeoutMs = 3 * 60_000,
  }: RunInterstitialOptions = {}
): Promise<InterstitialResult> => {
  let loaded = false;
  let shown = false;
  const fail = (
    stage: InterstitialFailureStage,
    e: unknown
  ): InterstitialResult => ({
    status: 'failed',
    stage,
    loaded,
    shown,
    message: errMessage(e),
  });

  let onShown: () => void = () => undefined;
  let onDismissed: () => void = () => undefined;
  let onShowFailed: (e: unknown) => void = () => undefined;
  const shownP = new Promise<void>((resolve, reject) => {
    onShown = resolve;
    onShowFailed = reject;
  });
  const dismissedP = new Promise<void>(resolve => {
    onDismissed = resolve;
  });
  // Avoid unhandled rejections if a phase is abandoned early.
  shownP.catch(() => undefined);

  const handles: ListenerHandle[] = [];
  const cleanup = async () => {
    await Promise.all(
      handles.map(h => Promise.resolve(h.remove()).catch(() => undefined))
    );
  };

  try {
    handles.push(
      await driver.onShowed(() => {
        shown = true;
        onEvent?.('shown');
        onShown();
      }),
      await driver.onDismissed(() => {
        onDismissed();
      }),
      await driver.onFailedToShow(e => onShowFailed(e))
    );

    onEvent?.('loading');
    try {
      await withTimeout(driver.prepare(options), loadTimeoutMs, 'Ad load');
    } catch (e) {
      return fail('load', e);
    }
    loaded = true;
    onEvent?.('loaded');

    try {
      await withTimeout(
        Promise.all([driver.show(), shownP]).then(() => undefined),
        showTimeoutMs,
        'Ad show'
      );
    } catch (e) {
      return fail('show', e);
    }

    try {
      await withTimeout(dismissedP, dismissTimeoutMs, 'Ad dismiss');
    } catch (e) {
      // Displayed but we never heard it close: do not count it as completed.
      return fail('show', e);
    }
    onEvent?.('dismissed');
    return { status: 'dismissed', loaded: true, shown: true };
  } catch (e) {
    return fail(loaded ? 'show' : 'load', e);
  } finally {
    await cleanup();
  }
};

const pluginInterstitialDriver: InterstitialDriver = {
  prepare: options => AdMob.prepareInterstitial(options),
  show: () => AdMob.showInterstitial(),
  onShowed: cb => AdMob.addListener(InterstitialAdPluginEvents.Showed, cb),
  onDismissed: cb =>
    AdMob.addListener(InterstitialAdPluginEvents.Dismissed, cb),
  onFailedToShow: cb =>
    AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, cb),
};

/* ------------------------------------------------------------------------ *
 * AdMob service
 * ------------------------------------------------------------------------ */

export class BhabiAdMob {
  private readonly adVisibleKey = 'bannerAdVisible';
  private ready: Promise<boolean> | null = null;
  private running: Promise<InterstitialResult> | null = null;
  bannerVisible = false;

  constructor(
    private readonly disable: boolean = false,
    private readonly env: AdEnv = buildEnv
  ) {
    // Initialisation (and therefore the consent form and the iOS tracking
    // prompt) is deferred until an ad is actually requested, after the app
    // has explained why an ad is about to play.
  }

  get platform(): AdPlatform {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  }

  get isTesting(): boolean {
    return isAdTesting(this.env);
  }

  /** True when this build can attempt native ads at all. */
  get nativeAdsAvailable(): boolean {
    return this.platform !== 'web' && !this.disable;
  }

  async isBannerVisible(): Promise<boolean> {
    const isVisible = await BhabiStorage.getItem(
      v => v,
      this.adVisibleKey
    ).catch(() => 'false');

    return isVisible === 'true' ? true : false;
  }

  /**
   * Initialises the SDK, then gathers consent before any ad request:
   * 1. Google UMP consent form when required (GDPR/US state privacy).
   * 2. iOS App Tracking Transparency prompt when not yet determined.
   * Resolves false when ads may not be requested. Memoised; a failed attempt
   * is retried next time.
   */
  initialize(): Promise<boolean> {
    if (!this.nativeAdsAvailable) return Promise.resolve(false);
    if (this.ready) return this.ready;
    this.ready = (async () => {
      await AdMob.initialize({ initializeForTesting: this.isTesting });
      let canRequestAds = true;
      try {
        let consent = await AdMob.requestConsentInfo();
        if (
          consent.isConsentFormAvailable &&
          consent.status === AdmobConsentStatus.REQUIRED
        ) {
          consent = await AdMob.showConsentForm();
        }
        canRequestAds = consent.canRequestAds !== false;
      } catch (e) {
        // UMP unreachable (offline etc.): the SDK will still serve
        // non-personalised ads where permitted.
        LoggerService.error('AdMob consent check failed: ', e);
      }
      if (this.platform === 'ios') {
        try {
          const att = await AdMob.trackingAuthorizationStatus();
          if (att.status === 'notDetermined') {
            await AdMob.requestTrackingAuthorization();
          }
        } catch (e) {
          LoggerService.error('ATT request failed: ', e);
        }
      }
      return canRequestAds;
    })().catch(e => {
      LoggerService.error('Error initializing AdMob: ', e);
      this.ready = null;
      return false;
    });
    return this.ready;
  }

  /**
   * Shows one interstitial for a play attempt and reports exactly what
   * happened. Concurrent calls share the same attempt.
   */
  showInterstitialForPlay(
    onEvent?: (e: InterstitialEvent) => void
  ): Promise<InterstitialResult> {
    if (this.running) return this.running;
    this.running = (async (): Promise<InterstitialResult> => {
      const { adId, isTesting } = resolveAdUnit(
        'interstitial',
        this.platform,
        this.env
      );
      if (!this.nativeAdsAvailable || !adId) {
        return {
          status: 'failed',
          stage: 'unavailable',
          loaded: false,
          shown: false,
          message: 'Ads are not available on this device.',
        };
      }
      onEvent?.('loading');
      const canRequestAds = await this.initialize();
      if (!canRequestAds) {
        return {
          status: 'failed',
          stage: 'consent',
          loaded: false,
          shown: false,
          message: 'Ads could not be requested with the current consent.',
        };
      }
      const result = await runInterstitial(
        pluginInterstitialDriver,
        { adId, isTesting },
        { onEvent }
      );
      LoggerService.info('Interstitial result', result);
      return result;
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  /** @deprecated use showInterstitialForPlay, which reports the outcome. */
  async interstitial(): Promise<void> {
    await this.showInterstitialForPlay();
  }

  async playAdOnLoop(
    // Default interval is 3 minutes
    interval = 3 * 60 * 1000
  ): Promise<NodeJS.Timeout> {
    await this.interstitial();
    return setTimeout(() => {
      this.playAdOnLoop(interval);
    }, interval);
  }

  /**
   * Banners are not shown anywhere in the current UI (they overlaid game
   * controls). Kept for compatibility; when used they also go through
   * consent and use test units outside production.
   */
  async banner(opt?: Partial<BannerAdOptions>): Promise<void> {
    if (!this.nativeAdsAvailable || this.bannerVisible) {
      return;
    }
    const { adId, isTesting } = resolveAdUnit('banner', this.platform, this.env);
    if (!adId || !(await this.initialize())) return;
    const options: BannerAdOptions = {
      adSize: BannerAdSize.BANNER,
      position: BannerAdPosition.TOP_CENTER,
      ...opt,
      adId,
      isTesting,
    };
    try {
      await AdMob.showBanner(options);
      BhabiStorage.setItem(this.adVisibleKey, 'true', isErr => {
        if (!isErr) {
          this.bannerVisible = true;
        }
      });
    } catch (e) {
      LoggerService.error(e);
    }
  }

  async removeBanner(): Promise<void> {
    // Nothing to hide unless this session showed a banner (avoids calling
    // into an uninitialised SDK on every account-shell mount).
    if (this.platform === 'web' || !this.bannerVisible) {
      return;
    }
    try {
      await AdMob.hideBanner();
      BhabiStorage.setItem(this.adVisibleKey, 'false', isErr => {
        if (!isErr) {
          this.bannerVisible = false;
        }
      });
    } catch (e) {
      LoggerService.error(e);
    }
  }
}

export const adMob = new BhabiAdMob();
